import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { fonts, colors, spacing, fontWeights, borderRadius } from '../theme';
import type { CashflowRow } from '../core/calculations';
import { ColumnInfoModal } from './ColumnInfoModal';

export interface M07FeeOverviewProps {
  basePremium: number;
  ridersTotal: number;
  totalPremiumNum: number;
  topUpAmount: number;
  totalTopUp: number;
  cashflowHigh: CashflowRow[];
}

const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export const M07FeeOverview: React.FC<M07FeeOverviewProps> = ({
  basePremium,
  ridersTotal,
  totalPremiumNum,
  topUpAmount,
  totalTopUp,
  cashflowHigh,
}) => {
  const [infoColumnKey, setInfoColumnKey] = useState<string | null>(null);

  const quarterlyValue = Math.round(basePremium / 4);
  const semiAnnualValue = Math.round(basePremium / 2);
  const ridersQuarterly = Math.round(ridersTotal / 4);
  const ridersSemiAnnual = Math.round(ridersTotal / 2);
  const totalQuarterly = Math.round(totalPremiumNum / 4);
  const totalSemiAnnual = Math.round(totalPremiumNum / 2);
  const topUpQuarterly = Math.round(topUpAmount / 4);
  const topUpSemiAnnual = Math.round(topUpAmount / 2);

  const totalRiders20Years = cashflowHigh.reduce((s, r) => s + r.riderPremium, 0);

  const renderTh = (label: string, colKey: string, widthStyle: any) => (
    <TouchableOpacity
      style={[styles.headerCellBtn, widthStyle, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }, { cursor: 'pointer' } as any]}
      onPress={() => setInfoColumnKey(colKey)}
      activeOpacity={0.6}
    >
      <Text style={[styles.detailTableHeaderCellText, { flexShrink: 1, textAlign: 'right' }]}>{label}</Text>
      <Text style={[styles.headerInfoBadge, { paddingLeft: 3 }]}>ⓘ</Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.subTabContent}>
      <ColumnInfoModal
        visible={!!infoColumnKey}
        columnKey={infoColumnKey}
        onClose={() => setInfoColumnKey(null)}
      />

      <Text style={styles.tableSubTitle}>KẾ HOẠCH ĐÓNG PHÍ THEO KỲ</Text>
      
      <View style={styles.responsiveTableWrapper}>
        <View style={styles.detailTable}>
          <View style={styles.detailTableHeader}>
            {renderTh('Kế hoạch đóng phí', 'premiumPaid', { width: 130 })}
            {renderTh('Năm', 'premiumPaid', { width: 95 })}
            {renderTh('Nửa năm', 'premiumPaid', { width: 95 })}
            {renderTh('Quý', 'premiumPaid', { width: 55 })}
            {renderTh('Tổng 20 năm', 'premiumPaid', { flex: 1 })}
          </View>
          <View style={styles.detailTableRow}>
            <Text style={[styles.detailTableCell, { width: 130, fontWeight: 'bold', textAlign: 'left' }]}>Phí cơ bản</Text>
            <Text style={[styles.detailTableCell, { width: 95, textAlign: 'right' }]}>{formatNumber(basePremium)}</Text>
            <Text style={[styles.detailTableCell, { width: 95, textAlign: 'right' }]}>{formatNumber(semiAnnualValue)}</Text>
            <Text style={[styles.detailTableCell, { width: 55, textAlign: 'right' }]}>{formatNumber(quarterlyValue)}</Text>
            <Text style={[styles.detailTableCell, { color: '#00A651', fontWeight: 'bold', textAlign: 'right' }]}>{formatNumber(basePremium * 20)}</Text>
          </View>
          <View style={styles.detailTableRow}>
            <Text style={[styles.detailTableCell, { width: 130, fontWeight: 'bold', textAlign: 'left' }]}>Phí bảo hiểm bổ trợ</Text>
            <Text style={[styles.detailTableCell, { width: 95, textAlign: 'right' }]}>{formatNumber(ridersTotal)}</Text>
            <Text style={[styles.detailTableCell, { width: 95, textAlign: 'right' }]}>{formatNumber(ridersSemiAnnual)}</Text>
            <Text style={[styles.detailTableCell, { width: 55, textAlign: 'right' }]}>{formatNumber(ridersQuarterly)}</Text>
            <Text style={[styles.detailTableCell, { color: '#00A651', fontWeight: 'bold', textAlign: 'right' }]}>
              {formatNumber(totalRiders20Years)}
            </Text>
          </View>
          <View style={[styles.detailTableRow, { backgroundColor: '#f0fdf4' }]}>
            <Text style={[styles.detailTableCell, { width: 130, fontWeight: 'bold', textAlign: 'left' }]}>Phí bảo hiểm định kỳ</Text>
            <Text style={[styles.detailTableCell, { width: 95, fontWeight: 'bold', textAlign: 'right' }]}>{formatNumber(totalPremiumNum)}</Text>
            <Text style={[styles.detailTableCell, { width: 95, fontWeight: 'bold', textAlign: 'right' }]}>{formatNumber(totalSemiAnnual)}</Text>
            <Text style={[styles.detailTableCell, { width: 55, fontWeight: 'bold', textAlign: 'right' }]}>{formatNumber(totalQuarterly)}</Text>
            <Text style={[styles.detailTableCell, { color: '#00A651', fontWeight: 'bold', textAlign: 'right' }]}>
              {formatNumber(basePremium * 20 + totalRiders20Years)}
            </Text>
          </View>
          <View style={styles.detailTableRow}>
            <Text style={[styles.detailTableCell, { width: 130, textAlign: 'left' }]}>Tổng phí đầu tư thêm</Text>
            <Text style={[styles.detailTableCell, { width: 95, textAlign: 'right' }]}>
              {topUpAmount > 0 ? formatNumber(topUpAmount) : '0'}
            </Text>
            <Text style={[styles.detailTableCell, { width: 95, textAlign: 'right' }]}>
              {topUpAmount > 0 ? formatNumber(topUpSemiAnnual) : '0'}
            </Text>
            <Text style={[styles.detailTableCell, { width: 55, textAlign: 'right' }]}>
              {topUpAmount > 0 ? formatNumber(topUpQuarterly) : '0'}
            </Text>
            <Text style={[styles.detailTableCell, { color: '#00A651', fontWeight: 'bold', textAlign: 'right' }]}>
              {topUpAmount > 0 ? formatNumber(totalTopUp) : '0'}
            </Text>
          </View>
        </View>
      </View>

      <Text style={[styles.tableSubTitle, { marginTop: spacing[4] }]}>PHÍ THEO MỖI NĂM</Text>
      
      <View style={styles.responsiveTableWrapper}>
        <View style={styles.detailTable}>
          <View style={[styles.detailTableHeader, { backgroundColor: '#00A651' }]}>
            {renderTh('NĂM HĐ', 'policyYear', { width: 55 })}
            {renderTh('TỔNG PHÍ', 'premiumPaid', { width: 110 })}
            {renderTh('PHÍ CƠ BẢN', 'premiumPaid', { width: 110 })}
            {renderTh('SP ĐÍNH KÈM', 'riderPremium', { flex: 1 })}
          </View>
          {cashflowHigh.map((row) => {
            const riderYr = row.riderPremium;
            const totalYr = basePremium + riderYr;
            return (
              <View key={row.year} style={[styles.detailTableRow, row.year === cashflowHigh.length && { backgroundColor: '#f0fdf4' }]}>
                <Text style={[styles.detailTableCell, { width: 45, color: '#0066cc', textAlign: 'right' }]}>{row.year}</Text>
                <Text style={[styles.detailTableCell, { width: 95, textAlign: 'right' }]}>{formatNumber(totalYr)}</Text>
                <Text style={[styles.detailTableCell, { width: 95, textAlign: 'right' }]}>{formatNumber(basePremium)}</Text>
                <Text style={[styles.detailTableCell, { textAlign: 'right' }]}>{formatNumber(riderYr)}</Text>
              </View>
            );
          })}
          <View style={[styles.detailTableRow, { backgroundColor: '#f0fdf4' }]}>
            <Text style={[styles.detailTableCell, { width: 45, fontWeight: 'bold', textAlign: 'right' }]}>Tổng</Text>
            <Text style={[styles.detailTableCell, { width: 95, fontWeight: 'bold', textAlign: 'right' }]}>
              {formatNumber(basePremium * 20 + totalRiders20Years)}
            </Text>
            <Text style={[styles.detailTableCell, { width: 95, fontWeight: 'bold', color: '#00A651', textAlign: 'right' }]}>{formatNumber(basePremium * 20)}</Text>
            <Text style={[styles.detailTableCell, { fontWeight: 'bold', color: '#00A651', textAlign: 'right' }]}>
              {formatNumber(totalRiders20Years)}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  subTabContent: {
    gap: spacing[2],
  },
  responsiveTableWrapper: {
    width: '100%',
    overflowX: 'auto' as any,
  },
  tableSubTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#003366',
    letterSpacing: 0.5,
    marginBottom: spacing[2],
  },
  detailTable: {
    width: '100%',
    minWidth: 380,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: borderRadius.sm,
    overflow: 'hidden',
  },
  detailTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#00A651',
  },
  detailTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    backgroundColor: '#ffffff',
  },
  detailTableCell: {
    flex: 1,
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[2],
    fontSize: 11,
    color: '#334155',
    textAlign: 'right',
    fontFamily: fonts.mono,
    fontVariant: ['tabular-nums'],
  },
  headerCellBtn: {
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[2],
  },
  detailTableHeaderCell: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    textAlign: 'right',
    fontFamily: fonts.body,
  },
  detailTableHeaderCellText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 11,
    fontFamily: fonts.body,
  },
  headerInfoBadge: {
    fontSize: 11,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 2,
    opacity: 0.9,
  },
});
